/**
 * Koulutukset — moduulin jaetut käsitteet (docs/KOULUTUKSET.md).
 *
 * Tuntikirjanpito on ensimmäinen asia, jonka tämä järjestelmä osasi, muttei
 * viimeinen. Koulutukset on toinen moduuli, ja se on täällä samasta syystä
 * kuin tunnitkin: **järjestelmä tuntee jo käyttäjät, roolit ja
 * sähköpostin.** Erillinen koulutusjärjestelmä tarkoittaisi toisen
 * käyttäjärekisterin, toisen kirjautumisen ja toisen paikan, josta
 * lähtevän työntekijän oikeudet unohtuu poistaa.
 *
 * Moduuli ei koske tunteihin. Se ei kirjaa aikaa, ei näy raporteissa eikä
 * vaikuta lukitukseen — koulutuksen käymiseen kuluva aika kirjataan
 * tunneiksi kuten mikä tahansa sisäinen työ, jos se halutaan kirjata.
 *
 * ## Mitä tässä tiedostossa on ja mitä ei
 *
 * Täällä ovat käsitteet, oletukset ja **tilalogiikka puhtaina
 * funktioina**: onko suoritus voimassa, vanhenemassa vai mennyt, ja
 * läpäisikö tentti. Ne ovat samat selaimessa ja palvelimella, ja ne on
 * voitava testata ilman kantaa.
 *
 * Täällä **ei ole yhtäkään oikeaa vastausta**. Kysymyspankki asuu
 * palvelimella (`apps/api/src/modules/trainings/catalog/`), koska tämä
 * paketti käännetään selaimen nippuun: oikeat vastaukset täällä
 * tarkoittaisivat, että tentin voi läpäistä lukemalla JavaScriptin.
 */
import { daysInMonth, formatIsoDate, parseIsoDate } from './time.js';
// --- Julkaisutila ----------------------------------------------------------
/**
 * Koulutuksen julkaisutila.
 *
 * `DRAFT` on koulutus, joka on synkronoitu koodista muttei vielä
 * kenenkään näkyvissä. Uusi koulutus syntyy aina luonnoksena — sisällön
 * saapuminen kantaan ja sen määrääminen pakolliseksi koko talolle ovat eri
 * päätöksiä, ja jälkimmäisen tekee ihminen hallintanäkymässä.
 *
 * `ARCHIVED` säilyttää suoritushistorian mutta poistaa koulutuksen
 * tarjonnasta. Poistoa ei ole: suoritettu koulutus on compliance-tieto,
 * ja sen katoaminen olisi juuri se asia, jota kilpailutuksessa kysytään.
 */
export const TRAINING_STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];
// --- Kohdennus -------------------------------------------------------------
/**
 * Kohdennuksen laji: rooli vai henkilö.
 *
 * Rooli on pääsääntö — "jokainen käyttäjä käy GDPR-koulutuksen" on
 * lause rooleista, ei nimistä, eikä sitä pidä joutua toistamaan jokaiselle
 * uudelle työntekijälle. Kohdennus rooliin seuraa käyttäjää automaattisesti:
 * uusi tili saa koulutuksensa samalla sekunnilla kun se luodaan.
 *
 * Henkilökohtainen kohdennus on poikkeus, jota ilman rooli olisi väärä
 * työkalu: yksi ihminen tarvitsee asiakassopimuksen takia koulutuksen,
 * jota muu talo ei tarvitse. Vaihtoehto olisi keksiä rooli yhtä ihmistä
 * varten, ja se rooli jäisi järjestelmään ikuisesti.
 */
export const TRAINING_AUDIENCE_KINDS = ['ROLE', 'USER'];
// --- Tenttiyritys ----------------------------------------------------------
/**
 * Tenttiyrityksen tila.
 *
 * Kolme tilaa, ei neljää. "Aikakatkaistu" ei ole oma tilansa vaan
 * `timedOut`-lippu tuloksen rinnalla: aika loppui, mutta annetut vastaukset
 * arvostellaan silti (katso `EXAM_TIMEOUT_GRADED`). Neljäs tila
 * tarkoittaisi, että jokainen raportti joutuisi päättämään erikseen,
 * lasketaanko se hylätyksi vai ei-miksikään.
 */
export const EXAM_ATTEMPT_STATUSES = ['IN_PROGRESS', 'PASSED', 'FAILED'];
/**
 * Umpeutunut tenttiyritys arvostellaan, ei mitätöidä.
 *
 * Kun aika loppuu, yritys suljetaan ja **määräaikaan mennessä annetut**
 * vastaukset arvostellaan. Vastaamatta jääneet ovat vääriä, joten ajan
 * loppuminen rankaisee jo itsessään — mitätöinti rankaisisi toista kertaa,
 * ja se osuisi useimmin siihen, jonka verkkoyhteys katkesi, ei siihen joka
 * ei osannut.
 *
 * Vakio on olemassa, jotta sääntö on nimetty siellä missä se on kirjoitettu
 * auki. Arvostelun tekee palvelin (`modules/trainings/service.ts`).
 */
export const EXAM_TIMEOUT_GRADED = true;
// --- Käyttäjän tila yhdessä koulutuksessa ----------------------------------
/**
 * Mitä yhden koulutuksen tila kertoo yhdestä ihmisestä.
 *
 * Tämä on se sarake, jota kilpailutuksessa katsotaan, ja siksi sen arvot
 * on valittu compliance-kysymyksen eikä käyttöliittymän ehdoilla:
 * kysymys on "onko koulutus voimassa", ei "mitä käyttäjä on viimeksi
 * tehnyt".
 *
 * `EXPIRING` on oma tilansa eikä `PASSED`in lisälippu: se on ainoa tila,
 * jossa mitään ei ole vielä rikki mutta jonkun on toimittava. Lippuna se
 * jäisi huomaamatta jokaisessa listassa, joka näyttää vain tilan.
 */
export const TRAINING_USER_STATUSES = [
    'NOT_STARTED',
    'IN_PROGRESS',
    'EXAM_OPEN',
    'PASSED',
    'EXPIRING',
    'EXPIRED',
];
// --- Oletukset ja rajat ----------------------------------------------------
/**
 * Uuden koulutuksen oletusasetukset.
 *
 * Nämä ovat **oletuksia, eivät sääntöjä**: jokainen on koulutuskohtaisesti
 * säädettävissä hallintanäkymästä, koska perehdytys ja tietoturvakoulutus
 * eivät ansaitse samaa rimaa. Oletukset ovat koodissa eivätkä kannassa,
 * jotta uusi koulutus syntyy järkevillä arvoilla ilman että kukaan täyttää
 * kahdeksaa kenttää käsin.
 */
export const TRAINING_DEFAULTS = {
    /** Läpäisyraja prosentteina. 80 % = 12/15 oikein. */
    passScorePercent: 80,
    /** Montako kysymystä yhteen tenttiin arvotaan pankista. */
    questionCount: 15,
    /** Aikaraja minuutteina. `null` = ei aikarajaa. */
    timeLimitMinutes: 30,
    /** Jäähy hylätyn yrityksen jälkeen. 0 = uusinta heti. */
    retryCooldownMinutes: 15,
    /**
     * Kuinka kauan aloitettu tentti elää, jos aikarajaa ei ole.
     * Tämä on "yhden session" sääntö: keskeneräistä tenttiä ei jatketa
     * seuraavana päivänä.
     */
    sessionMaxMinutes: 180,
    /** Suorituksen voimassaolo kuukausina. `null` = ei vanhene. */
    validityMonths: 12,
    /** Kuinka monta päivää ennen vanhenemista varoitetaan. */
    expiryWarningDays: 30,
    /** Onko materiaali käytävä läpi ennen tenttiä. */
    requireMaterialBeforeExam: true,
};
/** Monivalintatehtävän vaihtoehtojen määrä. Kiinteä: neljä, aina. */
export const EXAM_CHOICE_COUNT = 4;
/**
 * Oikeiden vaihtoehtojen määrä.
 *
 * Vähintään yksi, mutta ei kaikkia: kysymys, jonka jokainen vaihtoehto on
 * oikein, ei erottele mitään — sen läpäisee rastittamalla kaiken. Yläraja
 * on siis `EXAM_CHOICE_COUNT - 1`, ja se koskee myös yhden oikean
 * vastauksen kysymystä, joka on tämän saman säännön erikoistapaus.
 */
export const MAX_EXAM_CORRECT_CHOICES = EXAM_CHOICE_COUNT - 1;
export const MIN_PASS_SCORE_PERCENT = 50;
export const MAX_PASS_SCORE_PERCENT = 100;
export const MIN_EXAM_QUESTION_COUNT = 3;
export const MAX_EXAM_QUESTION_COUNT = 100;
export const MIN_EXAM_TIME_LIMIT_MINUTES = 5;
export const MAX_EXAM_TIME_LIMIT_MINUTES = 240;
export const MIN_EXAM_SESSION_MINUTES = 15;
/** Vuorokausi. Pidempi "sessio" ei ole sessio. */
export const MAX_EXAM_SESSION_MINUTES = 24 * 60;
export const MAX_RETRY_COOLDOWN_MINUTES = 7 * 24 * 60;
export const MIN_TRAINING_VALIDITY_MONTHS = 1;
/** Kymmenen vuotta. Sitä pidempi voimassaolo on käytännössä "ei vanhene". */
export const MAX_TRAINING_VALIDITY_MONTHS = 120;
export const MAX_EXPIRY_WARNING_DAYS = 365;
/** Koulutuksen koodin muoto: `GDPR`, `TIETOTURVA`, `DPA`. */
export const TRAINING_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,29}$/;
/** Osion ja kysymyksen avaimen muoto: `01-mika-on-gdpr`, `k12`. */
export const TRAINING_KEY_PATTERN = /^[a-z0-9][a-z0-9-]{0,59}$/;
// --- Arvostelu -------------------------------------------------------------
/**
 * Meniko yksi kysymys oikein.
 *
 * **Joukkojen yhtäsuuruus, ei osittaisia pisteitä.** Monivalinnassa, jossa
 * oikeita vaihtoehtoja on useampi, vastaus on oikein vain silloin kun
 * kaikki oikeat on valittu eikä yhtään väärää. Osittainen piste olisi
 * houkutteleva mutta väärä: "tietoturvaloukkaus on myös vahingossa
 * lähetetty sähköposti" on koko opetus, ja puolet siitä oivaltanut ei ole
 * puoliksi oikeassa vaan yhä siinä käsityksessä, että loukkaus on jotain,
 * mitä ulkopuolinen tekee.
 *
 * Yhden oikean vastauksen kysymys on tämän erikoistapaus, ei oma
 * sääntönsä: yhden alkion joukot ovat samat tai eivät.
 *
 * Vastaamatta jäänyt kysymys (tyhjä valinta) on väärin.
 */
export function isAnswerCorrect(selected, correct) {
    if (selected.length === 0 || correct.length === 0)
        return false;
    const valitut = new Set(selected);
    // Kaksi kertaa lähetetty sama avain ei saa muuttaa lopputulosta.
    if (valitut.size !== correct.length)
        return false;
    return correct.every((key) => valitut.has(key));
}
/**
 * Läpäisikö tentti.
 *
 * Vertailu tehdään **kokonaisluvuilla eikä pyöristetyllä prosentilla**.
 * 12/15 on 80 %, mutta 11/15 on 73,33 %, joka pyöristyisi näytöllä
 * 73 %:iin — ja 79,5 % pyöristyisi 80 %:iin. Jos läpäisy luettaisiin
 * näytettävästä luvusta, raja liikkuisi puoli prosenttiyksikköä sinne
 * päin, mihin pyöristys sattuu osumaan, ja sen huomaisi vasta se, joka
 * jäi väärälle puolelle.
 */
export function isExamPassed(correctCount, questionCount, passScorePercent) {
    if (questionCount <= 0)
        return false;
    return correctCount * 100 >= passScorePercent * questionCount;
}
/** Näytettävä tulos prosentteina. Vain esitystä varten — katso `isExamPassed`. */
export function examScorePercent(correctCount, questionCount) {
    if (questionCount <= 0)
        return 0;
    return Math.round((correctCount * 100) / questionCount);
}
/**
 * Montako oikeaa vastausta läpäisyyn tarvitaan.
 *
 * Käyttöliittymä kertoo tämän ennen tentin alkua: "12/15 oikein
 * läpäisee" on ymmärrettävämpi kuin "80 %", kun kysymyksiä on viisitoista.
 */
export function requiredCorrectAnswers(questionCount, passScorePercent) {
    return Math.ceil((questionCount * passScorePercent) / 100);
}
// --- Voimassaolo -----------------------------------------------------------
/**
 * Suorituksen vanhenemispäivä.
 *
 * **Kalenteripäivä, ei aikaleima** — sama valinta kuin kirjauspäivällä
 * (suunnitelma 4.2). "Koulutus on voimassa 31.8.2027 asti" on lause, jonka
 * voi kirjoittaa sopimusliitteeseen; "voimassa 31.8.2027 klo 14.23 asti"
 * ei ole, ja kellonaika tekisi vanhenemisesta aikavyöhykekysymyksen.
 *
 * Kuukausien lisäys **rajaa päivän kuukauden pituuteen**: 31.1. + 1 kk on
 * 28.2., ei 3.3. Ilman rajausta vuosittainen koulutus liukuisi eteenpäin
 * joka kierroksella, ja 31.8. suoritettu olisi kymmenen vuoden päästä
 * voimassa syyskuun puoliväliin.
 */
export function trainingExpiryDate(completedOn, validityMonths) {
    if (validityMonths === null)
        return null;
    const { year, month, day } = parseIsoDate(completedOn);
    const kuukausiaYhteensa = year * 12 + (month - 1) + validityMonths;
    const uusiVuosi = Math.floor(kuukausiaYhteensa / 12);
    const uusiKuukausi = (kuukausiaYhteensa % 12) + 1;
    return formatIsoDate(uusiVuosi, uusiKuukausi, Math.min(day, daysInMonth(uusiVuosi, uusiKuukausi)));
}
/** Päivien määrä `from`-päivästä `to`-päivään. Negatiivinen, jos `to` on menneisyydessä. */
export function daysUntil(from, to) {
    const alku = Date.UTC(...isoOsat(from));
    const loppu = Date.UTC(...isoOsat(to));
    return Math.round((loppu - alku) / 86_400_000);
}
function isoOsat(date) {
    const { year, month, day } = parseIsoDate(date);
    return [year, month - 1, day];
}
/**
 * Yhden ihmisen tila yhdessä koulutuksessa.
 *
 * Järjestys on merkitsevä ja se on compliance-järjestys, ei
 * käyttöliittymän: **voimassaolo ratkaistaan ensin.** Vanhentuneen
 * koulutuksen uudelleen aloittanut on `EXPIRED`, ei `IN_PROGRESS` — hän on
 * tekemässä oikeaa asiaa, mutta sopimusliitteen kannalta koulutus on
 * edelleen umpeutunut, ja raportin on sanottava se.
 */
export function trainingUserStatus(input) {
    if (input.completedOn !== null) {
        if (input.expiresOn === null)
            return 'PASSED';
        const paiviaJaljella = daysUntil(input.today, input.expiresOn);
        if (paiviaJaljella < 0)
            return 'EXPIRED';
        return paiviaJaljella <= input.expiryWarningDays ? 'EXPIRING' : 'PASSED';
    }
    if (input.hasOpenAttempt)
        return 'EXAM_OPEN';
    return input.sectionsRead > 0 ? 'IN_PROGRESS' : 'NOT_STARTED';
}
/**
 * Vaatiiko tila toimenpiteitä käyttäjältä.
 *
 * Vain pakollinen koulutus varoittaa. Vapaaehtoinen tarjolla oleva
 * koulutus on tarjous, ja tarjous, joka näkyy punaisena varoituksena
 * jokaisella sivulatauksella, opettaa sivuuttamaan varoitukset.
 *
 * `EXPIRING` ei ole puute vaan muistutus, joten se erotellaan omalla
 * funktiollaan — punainen palkki kuukautta ennen vanhenemista väittäisi,
 * että jokin on jo rikki.
 *
 * Määritelmä on **`!isTrainingCompliant`** eikä lista tiloja, ja se on
 * korjaus eikä tyyliseikka. Listana `EXAM_OPEN` jäi ulos, ja siitä seurasi
 * aukko: aloitettu ja hylätty tentti piilotti puuttuvan koulutuksen
 * varoituksen koko session ajaksi. Kesken oleva tentti ei ole suoritus,
 * eikä sen pidä näyttää siltä missään päin järjestelmää.
 */
export function isTrainingOverdue(status, mandatory) {
    return mandatory && !isTrainingCompliant(status);
}
export function isTrainingExpiring(status, mandatory) {
    return mandatory && status === 'EXPIRING';
}
/**
 * Onko koulutus compliance-mielessä kunnossa.
 *
 * `EXAM_OPEN` ei ole kunnossa: kesken oleva tentti ei ole suoritus, vaikka
 * se olisi kymmenen minuutin päässä siitä.
 */
export function isTrainingCompliant(status) {
    return status === 'PASSED' || status === 'EXPIRING';
}
//# sourceMappingURL=trainings.js.map